import { useEffect, useId, useState } from 'react';
import type { RecruitmentPanelProps } from './recruitment-extensions';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';

export interface GoogleSearchSettings {
  headlines: string[];
  descriptions: string[];
  keywords: { text: string; matchType: 'EXACT' | 'PHRASE' }[];
  geoTargets: { id: string; name: string }[];
  languageId: '1001';
  locationPresence: 'PRESENCE';
  searchPartners: false;
  dailyBudgetCents: number;
  maxCpcCents: number;
}
interface Placement {
  channel: string;
  placement: string;
  wording: string;
  assets: unknown[];
  sourceId: string;
  entryUrl: string;
  conversationVariant: string;
  googleSearch?: GoogleSearchSettings | null;
}
interface Plan {
  placements: Placement[];
  targeting: { locations: string[]; minimumAge: number; keywords: string[] };
  startsOn: string;
  endsOn: string;
  publicIdentity: string;
  allocationsCents: Record<string, number>;
  totalBudgetCents: number;
  managementFeeCents: number;
  testPlan: { limitCents: number; durationDays: number; comparison: string; pauseAtLimit: boolean };
}
interface Workspace {
  editable: boolean;
  revision: { snapshot: Plan; version: number } | null;
}
const lines = (text: string) =>
  text
    .split('\n')
    .map(value => value.trim())
    .filter(Boolean);
const initialSettings: GoogleSearchSettings = {
  headlines: [],
  descriptions: [],
  keywords: [],
  geoTargets: [],
  languageId: '1001',
  locationPresence: 'PRESENCE',
  searchPartners: false,
  dailyBudgetCents: 1000,
  maxCpcCents: 100,
};
export function GoogleSearchPreview({ settings }: { settings: GoogleSearchSettings }) {
  return (
    <section aria-label="Search-Vorschau" className="space-y-2 rounded-3xl border p-4 break-words">
      <p>Anzeige von Ivelion</p>
      <p className="font-semibold">{settings.headlines.join(' | ')}</p>
      <p>{settings.descriptions.join(' ')}</p>
      <p>
        Google kombiniert die freigegebenen Texte. Ivelion bleibt an erster Position. Automatische
        Texte, Zieladressenerweiterung und AI Max sind ausgeschaltet.
      </p>
      <p>{settings.keywords.map(k => `${k.text} (${k.matchType})`).join(', ')}</p>
      <p>
        {settings.geoTargets.map(g => g.name).join(', ')}. Deutsch, ab 18 Jahren, nur Personen in
        der Zielregion. Unbekanntes Alter ausgeschlossen.
      </p>
      <p>
        Nur Google-Suche, keine Suchpartner oder Zielgruppenlisten. Das Tagesbudget ist keine
        Gesamtobergrenze. Google-Berichte und Pausen können verzögert eintreffen.
      </p>
      <p>
        Durchschnittliches Tagesbudget {settings.dailyBudgetCents / 100} EUR. Höchstgebot pro Klick{' '}
        {settings.maxCpcCents / 100} EUR.
      </p>
    </section>
  );
}
export default function RecruitmentGoogle(props: RecruitmentPanelProps) {
  if (!props.campaign.channels.includes('google_search') || !props.transport.funding) return null;
  return <GoogleEditor key={`${props.campaign.id}:${props.campaign.version}`} {...props} />;
}
function GoogleEditor({ campaign, transport, onChange }: RecruitmentPanelProps) {
  const fieldId = useId();
  const api = transport.funding!;
  const path = `/campaigns/${campaign.id}/google-search`;
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [index, setIndex] = useState(0);
  const [headlines, setHeadlines] = useState('');
  const [descriptions, setDescriptions] = useState('');
  const [keywords, setKeywords] = useState('');
  const [regions, setRegions] = useState('');
  function selectPlacement(value: Plan, selected: number) {
    const settings = value.placements[selected]?.googleSearch ?? initialSettings;
    setIndex(selected);
    setHeadlines(settings.headlines.join('\n'));
    setDescriptions(settings.descriptions.join('\n'));
    setKeywords(settings.keywords.map(k => `${k.matchType}: ${k.text}`).join('\n'));
    setRegions(settings.geoTargets.map(g => `${g.id}: ${g.name}`).join('\n'));
  }
  useEffect(() => {
    let active = true;
    api
      .request<Workspace>(path)
      .then(value => {
        if (!active) return;
        setWorkspace(value);
        const initial: Plan = value.revision?.snapshot ?? {
          placements: [
            {
              channel: 'google_search',
              placement: 'search',
              wording: '',
              assets: [],
              sourceId: '',
              entryUrl: '',
              conversationVariant: '',
              googleSearch: initialSettings,
            },
          ],
          targeting: { locations: [], minimumAge: 18, keywords: [] },
          startsOn: campaign.startsOn ?? '',
          endsOn: campaign.endsOn ?? '',
          publicIdentity: 'Ivelion',
          allocationsCents: { google_search: 0 },
          totalBudgetCents: 0,
          managementFeeCents: 0,
          testPlan: { limitCents: 1, durationDays: 7, comparison: 'creative', pauseAtLimit: true },
        };
        setPlan(initial);
        selectPlacement(
          initial,
          Math.max(
            0,
            initial.placements.findIndex(p => p.channel === 'google_search')
          )
        );
      })
      .catch(() => {
        if (active) setError('Search-Einstellungen konnten nicht geladen werden.');
      });
    return () => {
      active = false;
    };
  }, [api, path, campaign.startsOn, campaign.endsOn]);
  if (!workspace || !plan)
    return error ? <p role="alert">{error}</p> : <p>Search-Einstellungen werden geladen…</p>;
  const placement = plan.placements[index];
  const settings: GoogleSearchSettings = {
    ...(placement.googleSearch ?? initialSettings),
    headlines: lines(headlines),
    descriptions: lines(descriptions),
    keywords: lines(keywords).map(line => ({
      text: line.replace(/^(EXACT|PHRASE):\s*/, ''),
      matchType: line.startsWith('PHRASE:') ? 'PHRASE' : 'EXACT',
    })),
    geoTargets: lines(regions).map(line => {
      const [id, ...name] = line.split(':');
      return { id: id.trim(), name: name.join(':').trim() };
    }),
  };
  function updatePlacement(patch: Partial<Placement>) {
    setPlan(
      current =>
        current && {
          ...current,
          placements: current.placements.map((p, i) => (i === index ? { ...p, ...patch } : p)),
        }
    );
  }
  function updateSettings(patch: Partial<GoogleSearchSettings>) {
    updatePlacement({ googleSearch: { ...settings, ...patch } });
  }
  async function save() {
    if (!plan || !workspace) return;
    setBusy(true);
    setError('');
    try {
      const placements = plan.placements.map((p, i) =>
        i === index ? { ...p, googleSearch: settings, wording: settings.headlines.join(' | ') } : p
      );
      const media = Object.values(plan.allocationsCents).reduce((sum, value) => sum + value, 0);
      const fee = Math.round(media * 0.3);
      await api.request(path, 'put', {
        ...plan,
        placements,
        expectedVersion: workspace.revision?.version ?? campaign.version,
        targeting: {
          locations: settings.geoTargets.map(g => g.name),
          minimumAge: 18,
          keywords: settings.keywords.map(k => k.text),
        },
        totalBudgetCents: media + fee,
        managementFeeCents: fee,
      });
      onChange(await transport.get(campaign.id));
    } catch {
      setError(
        'Speichern fehlgeschlagen. Texte, Suchbegriffe, Zielregionen, Budget und Revisionsstand prüfen. Ihre Eingaben bleiben erhalten.'
      );
    } finally {
      setBusy(false);
    }
  }
  const editable = workspace.editable;
  return (
    <section aria-label="Google Search vorbereiten" className="space-y-4 rounded-3xl border p-4">
      <h3 className="font-semibold">Google Search</h3>
      {error && <p role="alert">{error}</p>}
      {!editable && (
        <p>Gemeinsame Kampagne. Die verantwortliche Person bearbeitet diese Materialien.</p>
      )}
      <p>
        Änderungen benötigen eine neue Kundenannahme und Freigabe. Bezahlter Start benötigt
        zusätzlich bestätigte Konto-, Abrechnungs- und Einstiegsnachweise.
      </p>
      {plan.placements.filter(p => p.channel === 'google_search').length > 1 && (
        <label className="block">
          Anzeigenversion
          <select
            value={index}
            disabled={busy}
            onChange={e => {
              const updated = {
                ...plan,
                placements: plan.placements.map((p, i) =>
                  i === index ? { ...p, googleSearch: settings } : p
                ),
              };
              setPlan(updated);
              selectPlacement(updated, Number(e.target.value));
            }}
          >
            {plan.placements.map(
              (p, i) =>
                p.channel === 'google_search' && (
                  <option key={i} value={i}>
                    {p.sourceId || `Version ${i + 1}`}
                  </option>
                )
            )}
          </select>
        </label>
      )}
      <fieldset disabled={!editable || busy} className="space-y-3">
        <legend>Search-Materialien</legend>
        <div>
          <label className="block" htmlFor={`${fieldId}-headlines`}>
            Überschriften, eine pro Zeile
          </label>
          <Textarea
            id={`${fieldId}-headlines`}
            value={headlines}
            onChange={e => setHeadlines(e.target.value)}
          />
        </div>
        <p>3 bis 15 Überschriften, höchstens 30 Zeichen. Eine Überschrift lautet Ivelion.</p>
        <div>
          <label className="block" htmlFor={`${fieldId}-descriptions`}>
            Beschreibungen, eine pro Zeile
          </label>
          <Textarea
            id={`${fieldId}-descriptions`}
            value={descriptions}
            onChange={e => setDescriptions(e.target.value)}
          />
        </div>
        <p>2 bis 4 Beschreibungen, höchstens 90 Zeichen.</p>
        <div>
          <label className="block" htmlFor={`${fieldId}-keywords`}>
            Suchbegriffe, eine Zeile je Begriff
          </label>
          <Textarea
            id={`${fieldId}-keywords`}
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
          />
        </div>
        <p>
          EXACT: für genau passende Begriffe, PHRASE: für passende Wortgruppen. Ohne Präfix gilt
          EXACT.
        </p>
        <div>
          <label className="block" htmlFor={`${fieldId}-regions`}>
            Google-Zielregionen, ID und Name je Zeile
          </label>
          <Textarea
            id={`${fieldId}-regions`}
            value={regions}
            onChange={e => setRegions(e.target.value)}
            placeholder="1003854: Berlin"
          />
        </div>
        <p>
          Google muss die gewählten Regionen in Deutschland bestätigen. Die Vorschau zeigt die
          gespeicherten Namen.
        </p>
        <label className="block">
          Zieladresse
          <Input
            value={placement.entryUrl}
            onChange={e => updatePlacement({ entryUrl: e.target.value })}
          />
        </label>
        <label className="block">
          Quellenkennung
          <Input
            value={placement.sourceId}
            onChange={e => updatePlacement({ sourceId: e.target.value })}
          />
        </label>
        <label className="block">
          Gesprächseinstieg
          <Input
            value={placement.conversationVariant}
            onChange={e => updatePlacement({ conversationVariant: e.target.value })}
          />
        </label>
        <label className="block">
          Beginn
          <Input
            type="date"
            value={plan.startsOn}
            onChange={e => setPlan({ ...plan, startsOn: e.target.value })}
          />
        </label>
        <label className="block">
          Ende
          <Input
            type="date"
            value={plan.endsOn}
            onChange={e => setPlan({ ...plan, endsOn: e.target.value })}
          />
        </label>
        <label className="block">
          Google-Werbebudget in Cent
          <Input
            type="number"
            min={1}
            value={plan.allocationsCents.google_search}
            onChange={e =>
              setPlan({
                ...plan,
                allocationsCents: {
                  ...plan.allocationsCents,
                  google_search: Number(e.target.value),
                },
              })
            }
          />
        </label>
        <label className="block">
          Durchschnittliches Tagesbudget in Cent
          <Input
            type="number"
            min={1}
            value={settings.dailyBudgetCents}
            onChange={e => updateSettings({ dailyBudgetCents: Number(e.target.value) })}
          />
        </label>
        <label className="block">
          Höchstgebot pro Klick in Cent
          <Input
            type="number"
            min={1}
            value={settings.maxCpcCents}
            onChange={e => updateSettings({ maxCpcCents: Number(e.target.value) })}
          />
        </label>
        <label className="block">
          Testbudget in Cent
          <Input
            type="number"
            min={1}
            value={plan.testPlan.limitCents}
            onChange={e =>
              setPlan({
                ...plan,
                testPlan: { ...plan.testPlan, limitCents: Number(e.target.value) },
              })
            }
          />
        </label>
        <label className="block">
          Testdauer in Tagen
          <Input
            type="number"
            min={1}
            value={plan.testPlan.durationDays}
            onChange={e =>
              setPlan({
                ...plan,
                testPlan: { ...plan.testPlan, durationDays: Number(e.target.value) },
              })
            }
          />
        </label>
      </fieldset>
      <GoogleSearchPreview settings={settings} />
      <p className="break-all">Zieladresse: {placement.entryUrl}</p>
      <p>
        Google-Werbebudget {plan.allocationsCents.google_search / 100} EUR, zusätzlich 30 %
        Verwaltungsgebühr. Andere Kanalbudgets bleiben in der Revision sichtbar.
      </p>
      {editable && (
        <Button disabled={busy} onClick={() => void save()}>
          Search-Revision zur Annahme speichern
        </Button>
      )}
    </section>
  );
}
